import React, { Suspense, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

/** Matomo */
import { useMatomo } from "@datapunt/matomo-tracker-react";

/** MUI */
import { Container, Grid, IconButton, Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowUpward, Launch } from "@material-ui/icons";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import {
    getSelectedTreeItem,
    setExpandedItems,
    setOnTopId,
    setScrolledItem,
    setSelectedTreeItem,
} from "../../_store/TREE";
// eslint-disable-next-line
import { getDefaultData, setDefaultData, setRange, setYear } from "../../_store/MAIN";
import {
    getCurrentIndicator,
    getCurrentSection,
    setCurrentIndicator,
    setCurrentSection,
    setTableAreas,
    setTableData,
} from "../../_store/TABLE";

/** API */
import { getAreasFromAPI, getBasicDataFromAPI, getTableDataFromAPI, getUnitByIdFromAPI } from "../../api";

/** Helpers & utils */
import { booleanPointInPolygon, point } from "@turf/turf";
import GEO_WOJS from "../../components/Map/geodata/2023/wojewodztwa.json";
import { useTranslation } from "react-i18next";

/** UI */
const Preloader = React.lazy(() => import("../../components/common/Preloader"));
const PagePreloader = React.lazy(() => import("../../components/common/PagePreloader"));
const AppApiError = React.lazy(() => import("../../components/common/AppApiError"));
const LeftPanel = React.lazy(() => import("../../components/LeftPanel/LeftPanel"));
const PDF = React.lazy(() => import("../../components/PDF/PDF"));
const Map = React.lazy(() => import("../../components/Map/Map"));
const Info = React.lazy(() => import("../../components/Info/Info"));
const Table = React.lazy(() => import("../../components/Table/Table"));
const Pine = React.lazy(() => import("../../components/Pine/Pine"));
const Accordion = React.lazy(() => import("../../components/AccordionCharts/Accordion"));
const ChartBarLine = React.lazy(() => import("../../components/ChartBarLine/ChartBarLine"));
const Rank = React.lazy(() => import("../../components/Rank/Rank"));
const Search = React.lazy(() => import("../../components/Search/Search"));
const SearchIndicator = React.lazy(() => import("../../components/Search/SearchIndicator"));
const StackedBar = React.lazy(() => import("../../components/AccordionCharts/StackedBar"));
const MultiBar = React.lazy(() => import("../../components/AccordionCharts/BarMulti"));
const Linefill = React.lazy(() => import("../../components/AccordionCharts/Linefill"));

const Accordioned = React.lazy(() => import("../../components/AccordionCharts/Accordioned"));

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        marginBottom: theme.spacing(4),
        position: "relative",
    },
    MainPageWrapper: {
        width: "80%",
        margin: theme.spacing(0, 4, 1, 4),
        [theme.breakpoints.down("xs")]: {
            margin: theme.spacing(10, 0, 1, 0),
            width: "100%",
            padding: theme.spacing(0, 1.5),
        },
    },
    flexGridItem: {
        display: "flex",
        alignItems: "flex-start",
    },
    mainTitle: {
        flexGrow: 1,
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: "1.5rem",
        textAlign: "center",
        margin: theme.spacing(0, 1, 4, 0),
        borderBottom: `2px solid #CCC`,
        "& small": {
            textTransform: "uppercase",
            fontSize: "0.875rem",
        },
    },
    outlinedBtn: {
        border: "1px solid rgba(0,0,0,0.2)",
        padding: theme.spacing(2, 1),
        borderRadius: theme.spacing(0.5),
        margin: theme.spacing(0, 1, 0, 0),
        "& svg": {
            color: theme.palette.common.black,
        },
    },
    componentWrapper: {
        position: "absolute",
        top: 12,
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: -1,
    },
    componentHolder: {
        width: 1920,
        height: 1200,
    },
    toTopBtn: {
        position: "absolute",
        right: 100,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        width: 42,
        height: 42,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        [theme.breakpoints.down("xs")]: {
            position: "relative",
            right: "auto",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: theme.spacing(3),
        },
    },
    componentsHolder: {
        margin: theme.spacing(0, 0, 0, -1.5),
        "& > div": {
            marginBottom: theme.spacing(2),
        },
        // [theme.breakpoints.down("xs")]: {
        // },
    },
    infoBlock: {
        [theme.breakpoints.down("xs")]: {
            marginTop: 64,
        },
    },
    pagePreloader: {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        zIndex: 9999,
    },
    srOnly: {
        clip: "rect(0, 0, 0, 0)",
        width: 1,
        border: 0,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: "hidden",
        position: "absolute",
    },
}));

const MainPage = (props) => {
    // const { voivodship } = props.match.params;
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();
    const query = useQuery();
    const { t, i18n } = useTranslation();

    const getGeo = () => {
        const options = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
        };

        const success = (pos) => {
            const pt = point([pos.coords.longitude, pos.coords.latitude]);
            const woj = GEO_WOJS.features.find((feature) => {
                return booleanPointInPolygon(pt, feature);
            });
            const geo_woj_id = woj?.properties?.id_teryt || 7;

            getUnitById({
                id: geo_woj_id,
                title: "Portret województwa",
                expanded: ["2200000" + geo_woj_id, geo_woj_id.toString()],
                setError,
                dispatch,
                history,
                setOnTop: true,
            });
        };

        const error = (err) => {
            console.warn(`ERROR(${err.code}): ${err.message}\n Set default`);
            getUnitById({
                id: 7,
                title: "Portret województwa",
                expanded: ["expanded"],
                setError,
                dispatch,
                history,
                setOnTop: true,
            });
        };

        navigator.geolocation.getCurrentPosition(success, error, options);
    };

    // http://localhost:2925/?id=2&a=89&i=534        "Wybrane dane statystyczne" - "Urodzenia żywe na 1000 ludności [osoba] "
    // http://localhost:2925/2/89/534        "Wybrane dane statystyczne" - "Urodzenia żywe na 1000 ludności [osoba] "
    // http://localhost:2925/?id=2&a=84&i=227        "Ludność według wieku" - "Ludność w wieku 20–24 lata [osoba] "
    // http://localhost:2925/2/84/227        "Ludność według wieku" - "Ludność w wieku 20–24 lata [osoba] "

    /** territory */
    let id =
        query.get("id") ||
        query.get("t") ||
        query.get("tid") ||
        query.get("territoryId") ||
        props?.match?.params?.terrID;

    if (id && isNaN(Number(id))) {
        window.location.href = `/${subdomainToVoivodship(id?.toUpperCase())}`;
        id = false;
    }

    const terrID = id;

    /** area */
    const areaID = query.get("a") || query.get("aid") || query.get("areaId") || props?.match?.params?.areaID;

    /** indicator */
    const indicatorID =
        query.get("i") || query.get("iid") || query.get("indicatorId") || props?.match?.params?.indicatorID;

    /** Matomo */
    const { trackPageView } = useMatomo();
    React.useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    /** States */
    const [pdfRefs, setPdfRefs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [UrlParams, setUrlParams] = useState(false);
    /** - redux */
    const selectedItem = useSelector(getSelectedTreeItem);
    const currentSection = useSelector(getCurrentSection);
    const currentIndicator = useSelector(getCurrentIndicator);
    const defaultData = useSelector(getDefaultData);
    /** /end States */

    /** empty url */
    if (!terrID && !areaID && !indicatorID) {
        /** nothing saved */
        if (!selectedItem) {
            const voivodshipId = subdomainToVoivodship();
            if (voivodshipId) {
                getUnitById({
                    id: voivodshipId,
                    setError,
                    dispatch,
                    history,
                    setOnTop: true,
                });
                // history.replace(`/${voivodshipId}`)
            }
            getGeo();
        } else {
            if (!!selectedItem && !!currentIndicator && !!currentSection) {
                history.push(
                    `/${selectedItem?.id_teryt}/${currentSection?.id}/${currentIndicator?.id?.split("-")?.[1]}`
                );
            }
        }
    } else if (
        /** not empty url, but nothing changed (F%) */
        (terrID?.toString() === selectedItem?.id_teryt?.toString() &&
            areaID?.toString() === currentSection?.id?.toString() &&
            `${areaID}-${indicatorID}` === currentIndicator?.id?.toString()) ||
        loading
    ) {
        /** nothing to do */
    } else {
        setLoading(true);
        // dispatch(setDefaultData(null));
        processByUrlParams({
            terrID,
            areaID,
            indicatorID,
            dispatch,
            setCurrentSection,
            setCurrentIndicator,
            setTableData,
            setError,
            history,
        })
            .then((result) => {
                if (result.errors?.length) return;

                if (result?.units?.length) {
                    dispatch(
                        setSelectedTreeItem({
                            ...result?.units?.[0],
                            title: " ",
                        })
                    );
                    dispatch(setScrolledItem(result?.units?.[0]?.id_teryt + "|scroll"));
                }
                if (result?.areas?.length) {
                    dispatch(setTableAreas(result?.areas));
                }
                if (result?.tableData?.length) {
                    const { id, name, name_ang, rows } = result?.tableData?.[0];
                    const newIndicator = rows?.find((i) => i.id === `${areaID}-${indicatorID}`) || rows[0];
                    dispatch(setCurrentSection({ id, name, name_ang }));
                    dispatch(setCurrentIndicator(newIndicator));
                    dispatch(setTableData(result?.tableData));
                }

                if (!result?.errors?.length) {
                    // window?.location?.reload();
                    const _path = [
                        terrID || selectedItem?.id_teryt,
                        areaID || currentSection?.id,
                        indicatorID || currentIndicator?.id?.split("-")?.[1],
                    ];

                    // const _history = _path?.length === 3 ? "/" + _path?.join("/") : "/";
                    const _history = "/" + _path?.filter((f) => f)?.join("/");

                    // history.push("/");
                    history.push(_history);
                }
            })
            .catch((e) => console.log(e))
            .finally(() => {
                setUrlParams({ terrID, areaID, indicatorID });
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
                // processing = false;
            });
    }

    /** Handlers */
    const handleOnRef = ({ type, ref }) => {
        if (ref) {
            setPdfRefs({
                ...pdfRefs,
                [type]: ref,
            });
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };
    /** /end Handlers */

    return (
        <>
        <Typography variant={'h1'} className={classes.srOnly}>{t("GUS: Statystyczne Vademecum Samorządowca")}</Typography>
            <LeftPanel />
            <div className={classes.root}>
                {!!error && <AppApiError {...error} />}
                {/* {defaultData && !!selectedItem && !error && !loading ? ( */}

                {/* <pre>{JSON.stringify({defaultData})}</pre>
                <pre>{JSON.stringify({selectedItem})}</pre>
                <pre>{JSON.stringify({loading})}</pre> */}

                {/* {!!defaultData && !!selectedItem && !loading ? ( */}
                {!loading && selectedItem ? (
                    <Container maxWidth={false} className={classes.MainPageWrapper}>
                        <Grid container spacing={3} className={classes.componentsHolder}>
                            <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Search />
                                </Suspense>
                                {loading ? null : <PDF pdfRefs={pdfRefs} />}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Map onRef={(ref) => handleOnRef({ ref, type: "map" })} />
                                </Suspense>
                            </Grid>
                            <Grid item xs={12} md={6} className={classes.infoBlock}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    {/* <Info loading={loading} urlParams={UrlParams} /> */}
                                    <Info />
                                </Suspense>
                            </Grid>
                            <Grid item xs={12} style={{ height: "auto" }}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <SearchIndicator />
                                </Suspense>
                            </Grid>
                            <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Table urlParams={UrlParams} />
                                </Suspense>
                            </Grid>
                            <Grid item xs={12} style={{ position: "relative" }}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <ChartBarLine onRef={(ref) => handleOnRef({ ref, type: "bar" })} />
                                </Suspense>
                                <div className={classes.componentWrapper}>
                                    <ChartBarLine
                                        type={"line"}
                                        onRef={(ref) =>
                                            handleOnRef({
                                                ref,
                                                type: "pdfline",
                                            })
                                        }
                                    />
                                </div>

                                <div className={classes.componentWrapper}>
                                    <ChartBarLine type={"bar"} onRef={(ref) => handleOnRef({ ref, type: "pdfbar" })} />
                                </div>
                            </Grid>

                            <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Rank onRef={(ref) => handleOnRef({ ref, type: "rank" })} />
                                </Suspense>
                            </Grid>

                            {/* <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Pine onRef={(ref) => handleOnRef({ ref, type: "pine" })} />
                                </Suspense>
                            </Grid> */}
                            {/* <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Accordion onRef={(ref) => handleOnRef({ ref, type: "pine" })} />
                                </Suspense>
                            </Grid> */}
                            <Grid item xs={12}>
                                <Suspense fallback={<Preloader type={"linear"} />}>
                                    <Accordioned onRef={(ref) => handleOnRef({ ref, type: "pine" })} />
                                </Suspense>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    href={`https://smup.gov.pl/${
                                        i18n.language === "en" ? "en/" : "pl/"
                                    }basic?jt=${terrID}`}
                                    variant={"outlined"}
                                    fullWidth
                                    style={{
                                        backgroundColor: "#fff",
                                        textTransform: "capitalize",
                                        fontWeight: "bolder",
                                        fontSize: "1rem",
                                    }}
                                    startIcon={<Launch />}
                                    color={"primary"}
                                    target={"_blank"}
                                    aria-label={"przejdz do SMUP"}
                                >
                                    {t("System Monitorowania Usług Publicznych")}
                                </Button>
                            </Grid>
                        </Grid>
                        <IconButton
                            onClick={scrollToTop}
                            className={classes.toTopBtn}
                            aria-label={"wróć na początek strony"}
                        >
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </Container>
                ) : (
                    !!loading && (
                        <div className={classes.pagePreloader}>
                            <PagePreloader />
                        </div>
                    )
                )}
            </div>
        </>
    );
};

// http://dolnoslaskie.svs.local:2925/

const getUnitById = async ({ id, title, expanded, setError, dispatch, history, setOnTop = false, redirect = true }) => {
    if (isNaN(Number(id))) return;

    await getUnitByIdFromAPI({ terrID: id }).then((result) => {
        if (result.message?.type === "Error") {
            setError(result);
            // return false;
        } else {
            if (result.length) {
                dispatch(setSelectedTreeItem({ ...result[0], title: title || " " }));
                dispatch(setScrolledItem(result[0].id_teryt + "|scroll"));
                setOnTop && dispatch(setOnTopId(result[0].woj));
                expanded?.length && dispatch(setExpandedItems([result[0]?.woj?.toString()]));
            }

            redirect && history.push("/");
        }
    });
};

const processByUrlParams = async ({ terrID, areaID, indicatorID, setError, history }) => {
    console.clear();
    const errors = [];

    const units =
        terrID &&
        (await getUnitByIdFromAPI({ terrID })
            .then((result) => {
                if (result.message?.type === "Error") {
                    errors.push(result);
                    // setError(result);
                    history.push("/wrong-id?err=territoryID");
                    return;
                }
                return result;
            })
            .catch((e) => console.log(e)));

    const areas =
        areaID &&
        (await getAreasFromAPI({ terrID })
            .then((result) => {
                if (result.message?.type === "Error") {
                    errors.push(result);
                    // setError(result);
                    return result;
                }

                const _areaIds = result?.map((r) => r?.id?.toString());

                if (!_areaIds?.includes(areaID?.toString())) {
                    if (_areaIds?.length) {
                        errors.push("wrong-id?err=areaID");
                        history.push(`/${terrID}/${_areaIds[0]}`);
                    } else {
                        errors.push("wrong-id?err=areaID");
                        history.push("/wrong-id?err=areaID");
                    }
                    return;
                }
                return result;
            })
            .catch((e) => console.log(e)));

    const tableData =
        areaID &&
        (await getTableDataFromAPI({ terrID, areaID })
            .then((result) => {
                if (result.message?.type === "Error") {
                    errors.push(result);
                    // setError(result);
                    return result;
                }

                const _indicatorIds = result?.rows?.map((r) => r?.id?.toString());

                if (!_indicatorIds?.includes(`${areaID}-${indicatorID}`)) {
                    if (_indicatorIds?.length) {
                        errors.push("wrong-id?err=indicatorID");
                        history.push(`/${terrID}/${areaID}/${_indicatorIds?.[0]?.split("-")?.[1]}`);
                    } else {
                        errors.push("wrong-id?err=indicatorID");
                        history.push("/wrong-id?err=indicatorID");
                    }
                    return false;
                }

                result.rows = result?.rows?.map((row) => {
                    const _rowKeys = Object.keys(row);
                    const rowValues = Object.values(row).filter((r) => typeof r === "number" && r !== null);
                    const precision = decimalCount(rowValues.find((r) => String(r).includes(".")) || rowValues[0]);

                    let _extRow = {};
                    _rowKeys?.forEach((key) => {
                        _extRow[key] =
                            !row[key]?.val && (typeof row[key] === "number" || row[key] === null)
                                ? {
                                      val: row[key],
                                      pre: precision,
                                      fid: row[key] === null ? 5 : 1,
                                  }
                                : row[key];
                    });
                    return _extRow;
                });

                result = [result];

                return result;
            })
            .catch((e) => console.log(e))
            .finally(() => {}));

    return { units, areas, tableData, errors };
};

const S2V = {
    DOLNOSLASKIE: "1",
    "KUJAWSKO-POMORSKIE": "2",
    LUBELSKIE: "3",
    LUBUSKIE: "4",
    LODZKIE: "5",
    MALOPOLSKIE: "6",
    MAZOWIECKIE: "7",
    OPOLSKIE: "8",
    PODKARPACKIE: "9",
    PODLASKIE: "10",
    POMORSKIE: "11",
    SLASKIE: "12",
    SWIETOKRZYSKIE: "13",
    "WARMINSKO-MAZURSKIE": "14",
    WIELKOPOLSKIE: "15",
    ZACHODNIOPOMORSKIE: "16",
};

const subdomainToVoivodship = (SUBDOMAIN = false) => {
    SUBDOMAIN = SUBDOMAIN || window.location.hostname.split(".").reverse()[2]?.toUpperCase();
    const VOIVODSHIP = S2V[SUBDOMAIN] || "wrong-id";

    return VOIVODSHIP;
};

const decimalCount = (num) => {
    const numStr = String(num);
    if (numStr.includes(".")) {
        return numStr.split(".")[1].length;
    }
    return 0;
};

export default MainPage;
